import classnames from 'classnames';
import omit from 'omit.js';
import PropTypes from 'prop-types';
import React, { CSSProperties, ReactElement, ReactNode } from 'react';
import { prefix, setComponentSize, WebUI } from "../../wui-core/src/index"
import { RadioGroupProps, RadioGroupState, RadioValue, MockRadio, RadioContext, RadioProps } from './iRadio';
import Radio from './Radio';
import RadioButton from './RadioButton';
import Dropdown from '../../wui-dropdown/src';
import Popover from '../../wui-popover/src';
import Menu from '../../wui-menu/src';
import Button from '../../wui-button/src';
import Icon from '../../wui-icon/src';
import { OrNull } from '../../wui-core/src/types/iCore';
import { getLangInfo } from '../../wui-locale/src/tool';
import i18n from '../../wui-calendar/src/i18n/lang';
import { WithConfigConsumer } from '../../wui-provider/src/context'
const { Item } = Menu;

const sizeMap = {
    sm: 'sm',
    md: 'md',
    lg: 'lg',
    small: 'sm',
    middle: 'md',
    large: 'lg',
}
const noop = () => {
}

const defaultProps = {
    Component: 'div',
    clsPrefix: `${prefix}-radio-group`,
    defaultValue: '',
    antd: false,
    onChange: noop,
    name: '',
    children: <div></div>,
    readOnly: false,
    maxCount: false,
    tiled: false,
    tiledFooter: null,
    showMoreText: false,
    browser: false,
    browserShow: false
};

/**
 * 与子Radio通信
 */
const childContextTypes = {
    radioGroup: PropTypes.object
}

const getValues = (children: any): RadioValue[] => {
    let array: RadioValue[] = []
    if (!children || (Array.isArray(children) && children.length === 0)) {
        return array;
    }
    if (!Array.isArray(children)) { // 如果只有一个元素，不是数组，则放到数组中
        children = [children]
    } else if (children.some((child: any) => Array.isArray(child))) { // 如果数组中还有数组
        let res: (React.ReactElement | MockRadio)[] = []
        children.forEach((child: any) => {
            if (Array.isArray(child)) {
                res = res.concat(child)
            } else {
                res.push(child)
            }
        })
        children = res
    }
    children = children.map((child: any) => {
        if (child) return child
        return {
            props: {
                value: ''
            }
        }
    })
    if (children.length > 1) {
        children.map((item: any) => {
            array.push(item.props.value)
        })
    } else if (children.length === 1) {
        array.push(children[0].props.value)
    } else {
        array.push(children.props.value);
    }
    return array;
}

@WithConfigConsumer()
@WebUI({ name: "radio-group", defaultProps })
class RadioGroup extends React.Component<RadioGroupProps, RadioGroupState> {
	static defaultProps = { ...defaultProps }
	static childContextTypes = childContextTypes;
	childNodesResizeObserver: ResizeObserver | null = null;;
	parentResizeObserver: OrNull<ResizeObserver> = null;
	dobounceFillSpaceFun: (() => void | undefined) | undefined | null;
	radioGroup: HTMLElement | null = null;
	constructor(props: RadioGroupProps, context: RadioContext<RadioGroupProps>) {
	    super(props, context);
	    const initValue = props.value ?? (props.selectedValue ?? props.defaultValue);
	    this.state = {
	        focusValue: '',
	        selectedValue: initValue,
	        maxCount: -1,
	        popoverMaxWidth: 100
	    }
	}

	getValues = () => {
	    let array: RadioValue[] = []
	    let children: any = this.props.children;
	    if (!children || (Array.isArray(children) && children.length === 0)) {
	        return array;
	    }
	    if (!Array.isArray(children)) { // 如果只有一个元素，不是数组，则放到数组中
	        children = [children]
	    } else if (children.some((child: any) => Array.isArray(child))) { // 如果数组中还有数组
	        let res: (React.ReactElement | MockRadio)[] = []
	        children.forEach((child: any) => {
	            if (Array.isArray(child)) {
	                res = res.concat(child)
	            } else {
	                res.push(child)
	            }
	        })
	        children = res
	    }
	}

	getMaxCount = () => {
	    if (this.props.maxCount === true && !this.props.browser) {
	        this.dobounceFillSpaceFun = this.throttle(() => this.setInnerMaxCount(), 200, true)
	        let parent = this.radioGroup!.parentNode;
	        if (parent && parent.nodeName.toUpperCase() === 'FORM') {
	            parent = parent.parentNode;
	        }
	        if (parent) {
	            this.parentResizeObserver = new ResizeObserver(() => {
	                this.setState({
	                    maxCount: -1
	                }, () => {
	                    this.dobounceFillSpaceFun && this.dobounceFillSpaceFun()
	                });
	            });
	            this.parentResizeObserver.observe(parent as Element)
	        }
	        if (this.props.tiled && this.radioGroup) {
	            this.childNodesResizeObserver = new ResizeObserver(entries => {
	                for (let entry of entries) {
					  if (entry.target === this.radioGroup) {
	                        this.setState({
						  popoverMaxWidth: entry.contentRect.width - 3,
	                        });
					  }
	                }
				  });
				  this.childNodesResizeObserver.observe(this.radioGroup);
	        }
	    }
	}

	componentDidMount() {
	    let children = this.props.children
	    let array = getValues(children);
	    if (array.indexOf(this.props.selectedValue as RadioValue) == -1) {
	        this.setState({
	            focusValue: array[0]
	        })
	    }
	    this.getMaxCount();
	    setTimeout(() => { // 确保在组件挂载后执行，以保证获取父容器宽度
	        this.updateButtonLabels();
	    }, 0);
	}

	updateButtonLabels = () => { // 该组件负责控制按钮最大长度，避免样式问题
	    if (!this.radioGroup) return;
	    // 检查是否有兄弟元素
	    const hasSiblings = this.radioGroup.previousElementSibling || this.radioGroup.nextElementSibling;
	    const parentWidth = this.radioGroup.parentElement?.clientWidth || 400;
	    // 只在没有兄弟元素时应用自适应宽度，以避免被兄弟元素挤压
	    if (!hasSiblings && parentWidth > 100) {
	        const maxButtonWidth = Math.min(parentWidth * 0.4, 80);
	        // 找到所有按钮元素
	        const buttons = this.radioGroup.querySelectorAll('.wui-radio-button');
	        buttons.forEach((button) => {
	            if (button instanceof HTMLElement) {
	                // 获取按钮内的label和input元素
	                const labelElement = button.querySelector('.wui-radio-button-label');
	                const inputElement = button.querySelector('input[type="radio"]');
	                if (labelElement instanceof HTMLElement && inputElement instanceof HTMLElement) {
	                    // 检查label内容是否为纯文本
	                    const isOnlyText = (node: Node): boolean => {
	                        const children = Array.from(node.childNodes);
	                        // 直接是文本
	                        if (children.length === 1 && children[0].nodeType === Node.TEXT_NODE) {
	                            return true;
	                        }
	                        // 用户自定义了些东西
	                        return false;
	                    };
	                    if (isOnlyText(labelElement)) {
	                        labelElement.classList.add(`${prefix}-radio-button-label-is-text`);
	                    }
	                    const fullText = labelElement.textContent || '';
	                    labelElement.style.maxWidth = `${maxButtonWidth}px`;
	                    labelElement.setAttribute('title', fullText);
	                    // 给input设置title
	                    inputElement.setAttribute('title', fullText);
	                    // 给按钮本身也设置title以确保覆盖全部情况
	                }
	            }
	        });
	    }
	}

	static getDerivedStateFromProps(nextProps: RadioGroupProps, _prevState: RadioGroupState) {
	    const newState: Partial<RadioGroupState> = {};

	    // 处理 selectedValue 和 value 的更新
	    if ('selectedValue' in nextProps || 'value' in nextProps) {
	        newState.selectedValue = nextProps.selectedValue !== undefined ? nextProps.selectedValue : nextProps.value;
	    }
	    const array = getValues(nextProps.children);
	    // 处理 focusValue 的更新
	    if (array.indexOf(nextProps.selectedValue as RadioValue) === -1 ||
			(nextProps.value && array.indexOf(nextProps.value) === -1)) {
	        newState.focusValue = array[0];
	    } else {
	        newState.focusValue = '';
	    }

	    return Object.keys(newState).length ? newState : null;
	}

	componentDidUpdate(prevProps: RadioGroupProps) {
	    // browser模式解除时的maxCount重新计算并添加
	    if (
	        ('browser' in this.props && prevProps.browser !== this.props.browser)
	        || prevProps.options !== this.props.options
	        || prevProps.children !== this.props.children
	    ) {
	        setTimeout(() => {
	            this.updateButtonLabels();
	        }, 0);
	    }

	    if (
	        'browser' in this.props &&
	        prevProps.browser === true &&
	        this.props.browser === false &&
	        this.props.maxCount === true
	    ) {
	        setTimeout(() => {
	            this.getMaxCount();
	        }, 0);
	    }
	}

	componentWillUnmount(): void {
	    this.dobounceFillSpaceFun = null;
	    if (this.parentResizeObserver) {
	        this.parentResizeObserver.disconnect();
	    }
	}

	throttle = (fn: any, delay: any, isImmediate = true) => {
	    // isImmediate 为 true 时使用前缘节流，首次触发会立即执行，为 false 时使用延迟节流，首次触发不会立即执行
	    let last = Date.now();
	    let timeoutId: any = null;
	    return function() {
	        let now = Date.now();
	        if (isImmediate) {
	            fn.apply(this, arguments);
	            isImmediate = false;
	            last = now;
	        }
	        if (now - last >= delay) {
	            fn.apply(this, arguments);
	            last = now;
	        } else {
	            if (timeoutId) {
	                clearTimeout(timeoutId);
	            }
	            timeoutId = setTimeout(() => {
	                last = now;
	                fn.apply(this, arguments);
	            }, delay);
	        }
	    }
	}

	setInnerMaxCount = () => {
	    const { maxCount: preMaxCount, showMoreText } = this.props;
	    if (!preMaxCount || !this.radioGroup) {
	        return;
	    }
	    this.updateButtonLabels();
	    let parent = this.radioGroup!.parentElement;
	    if (parent && parent.nodeName.toUpperCase() === 'FORM') {
	        parent = parent.parentNode as HTMLElement;
	    }
	    const parentWith = parent?.clientWidth;
	    const childNodes = this.radioGroup!.childNodes as any;
	    let maxCount = this.state.maxCount;
	    let dropDownWidth = 20;
	    let langTemp = getLangInfo(this.props.locale, i18n, 'calendar').lang;
	    if (showMoreText) {
	        switch (langTemp) {
	            case 'en-us':
	                dropDownWidth = 63;
	                break;
	            case 'vi-vn':
	                dropDownWidth = 92;
	                break;
	            case 'id-id':
	                dropDownWidth = 109;
	                break;
	            case 'ar-sa':
	                dropDownWidth = 52; // 这部分需要调整，阿拉伯语的文件没写完，目前语言设置为阿拉伯语仍然会有一部分显示为中文，所以等写完再调
	                break;
	            default:
	                dropDownWidth = 58;
	                break;
	        }
	    }
	    const isRTL = this.props.dir === 'rtl';
	    let totalWidth = childNodes[0]?.clientWidth;
	    childNodes.forEach((child: HTMLElement, index: number) => {
	        if (isRTL) {
	            if (maxCount < 0 && index !== 0) { // && child.offsetLeft < childNodes[0].offsetLeft + 5 && child.offsetLeft > childNodes[0].offsetLeft - 5
	                const style = childNodes[index].currentStyle || getComputedStyle(childNodes[index]);
	                totalWidth += childNodes[index].clientWidth + (parseInt(style.marginRight) || 0) + (parseInt(style.marginLeft) || 0);
	                if (totalWidth > parentWith!) {
	                    maxCount = index - 1
	                    return this.setState({
	                        maxCount: dropDownWidth > 90 ? index - 3 : (dropDownWidth > 40 ? index - 2 : index - 1) // 之前的设置仍然造成按钮换行，因此采用了更稳妥的措施
	                    })
	                }
	                // if (childNodes[index - 1].offsetLeft - childNodes[index - 1].clientWidth - (parseInt(style.marginLeft) || 0) < dropDownWidth) {
	                //     maxCount = index - 2
	                //     return this.setState({
	                //         maxCount: dropDownWidth > 55 ? index - 3 : index - 2
	                //     })
	                // }
	            } else if (maxCount < 0 && index === 0) { // 第一个元素超出了父容器宽度
	                const style = childNodes[index].currentStyle || getComputedStyle(childNodes[index]);
	                if (child.clientWidth > parentWith! || child.clientWidth + (parseInt(style.marginRight) || 0) > parentWith!) {
	                    maxCount = 0
	                    return this.setState({
	                        maxCount: 0
	                    })
	                }
	            }
	        } else {
	            if (maxCount < 0 && index !== 0 && child.offsetLeft < childNodes[0].offsetLeft + 5 && child.offsetLeft > childNodes[0].offsetLeft - 5) {
	                const style = childNodes[index - 1].currentStyle || getComputedStyle(childNodes[index - 1]);
	                if (childNodes[index - 1].offsetLeft + childNodes[index - 1].clientWidth + (parseInt(style.marginRight) || 0) + dropDownWidth + 3 > parentWith!) { // 由于负margin等原因，实践中仍然观察到按钮换行，因此加3px
	                    maxCount = index - 1
	                    return this.setState({
	                        maxCount: dropDownWidth > 90 ? index - 3 : (dropDownWidth > 40 ? index - 2 : index - 1) // 之前的设置仍然造成按钮换行，因此采用了更稳妥的措施
	                    })
	                }
	                maxCount = index - 1
	                return this.setState({
	                    maxCount: index
	                })
	            } else if (maxCount < 0 && index === 0) { // 第一个元素超出了父容器宽度
	                const style = childNodes[index].currentStyle || getComputedStyle(childNodes[index]);
	                if (child.clientWidth > parentWith! || child.clientWidth + (parseInt(style.marginRight) || 0) > parentWith!) {
	                    maxCount = 0
	                    return this.setState({
	                        maxCount: 0
	                    })
	                }
	            }
	        }
	    });
	}

	handleChange = (value: RadioValue | React.ChangeEvent, event?: React.ChangeEvent, isDropDownChange?: boolean) => {
	    let { onChange, antd, selectedValue: preVal, onClick } = this.props;
	    let val = value
	    let currentVal = antd ? (value as React.ChangeEvent<HTMLInputElement>).target?.value : value;
	    onClick && onClick(value, event)

	    if (currentVal === preVal) return;
	    if (!('selectedValue' in this.props)) {
	        this.setState({
	            selectedValue: val
	        })
	    }
	    const args: [value: RadioValue | React.ChangeEvent | React.MouseEvent<HTMLElement>, event?: | React.ChangeEvent | React.MouseEvent<HTMLElement>, options?: {maxCount?: number}] = [value, event];
	    if (isDropDownChange) {
	        args.push({maxCount: this.state.maxCount})
	    }
	    onChange && onChange(...args);
	}

	/**
	 * 一旦外层change方法触发本身props发生改变，则调用getChildContext更新与子Radio的通信信息（radioGroup）
	 */

	getChildContext() {
	    const { name, size } = this.props;
	    let { selectedValue } = this.state;
	    let onChange = this.handleChange;
	    return {
	        radioGroup: {
	            name, selectedValue, onChange, size, focusValue: this.state.focusValue
	        }
	    }
	}
	selectDropItem: any = ({domEvent, selectedKeys: [selectedKey]} : {domEvent: any, selectedKeys: string[]}) => {
	    if (!this.props.readOnly && !this.props.disabled) { // 只在非只读禁用状态下允许点击
	        this.handleChange(selectedKey, domEvent as any, true)
	    }
	}
	renderOptionContent = ({ option, mergeStyle }: { option: (RadioProps & { label: ReactNode } | string); mergeStyle: CSSProperties }) => {
	    const { optionType, readOnly, browser, browserShow, disabled, antd } = this.props;
	    const props = {
	        readOnly: readOnly || (browser && browserShow),
	        disabled: disabled,
	        antd: antd
	    };
	    if (typeof option === 'string') { // 应用按钮长度控制，给按钮添加title以展示全部内容
	        return optionType === "button" ? <RadioButton {...props} value={option} style={mergeStyle}>
	            {option}
	        </RadioButton> : <Radio value={option} {...props}>{option}</Radio>;
	    }
	    const { value, label, style, ...otherProps } = option;
	    return optionType === "button" ? <RadioButton value={value} style={mergeStyle} {...props} {...otherProps} >
	            {label}
	    </RadioButton> : <Radio value={value} {...props} {...otherProps}>{label}</Radio>;
	}
	renderChildContent = ({ option, mergeStyle }: { option: ReactElement<RadioProps>; mergeStyle: CSSProperties }) => {
	    const { readOnly, browser, browserShow, disabled, antd } = this.props;
	    return React.cloneElement(option,
	        {
	            // disabled: get(child, "props.disabled") || disabled,
	            disabled: option?.props?.disabled || disabled,
	            readOnly: readOnly || (browser && browserShow), // RadioGroup下的Radio，都只使用父节点的readOnly属性，子节点不能覆盖父节点的readOnly。单独使用的Radio使用自己的readOnly
	            antd,
	            style: mergeStyle
	        }
	    )
	}
	renderContentFromCount = ({ spaceStyle, menuKey, menuContent, style, arrayLength, optionType, option, index, isOption, menuItem }: {
		spaceStyle: CSSProperties; menuKey: string; menuContent: string; style: CSSProperties; arrayLength: number; optionType?: string; option?: ReactElement<RadioProps> | RadioProps | string; index: number; isOption: boolean; menuItem: any[];
	}) => {
	    const { maxCount } = this.props;
	    let mergeStyle = spaceStyle;
	    mergeStyle = style || {};
	    if (index !== arrayLength - 1 && spaceStyle) {
	        mergeStyle = { ...spaceStyle, ...mergeStyle }
	    }
	    if (!maxCount || optionType !== "button") {
	        return isOption ? this.renderOptionContent({ option, mergeStyle } as { option: (RadioProps & { label: ReactNode } | string); mergeStyle: CSSProperties }) : this.renderChildContent({ option, mergeStyle } as { option: ReactElement<RadioProps>; mergeStyle: CSSProperties })
	    }
	    if (typeof maxCount === 'number' || (typeof this.state.maxCount === 'number' && this.state.maxCount > -1)) {
	        const mergeMaxCount = this.state.maxCount === 0 ? this.state.maxCount : (this.state.maxCount || maxCount as number)
	        if ((mergeMaxCount > 0 && mergeMaxCount < arrayLength && index >= mergeMaxCount) || mergeMaxCount === 0) {
	            menuItem.push(
	                <Item
	                    key={menuKey as string}
	                    className={classnames({
	                        'is-checked': this.state.selectedValue === menuKey,
	                        [`${prefix}-radio-readonly`]: this.props.readOnly || (typeof option === 'object' && 'readOnly' in option && option.readOnly),
	                        [`${prefix}-radio-disabled`]: this.props.disabled || (typeof option === 'object' && 'disabled' in option && option.disabled),
	                    })}
	                    disabled={this.props.disabled || (typeof option === 'object' && 'disabled' in option && option.disabled)}
	                >
	                    {menuContent}
	                </Item>
	            )
	            return null
	        } else {
	            return isOption ? this.renderOptionContent({ option, mergeStyle } as { option: (RadioProps & { label: ReactNode } | string); mergeStyle: CSSProperties }) : this.renderChildContent({ option, mergeStyle } as { option: ReactElement<RadioProps>; mergeStyle: CSSProperties })
	        }
	    } else {
	        return isOption ? this.renderOptionContent({ option, mergeStyle } as { option: (RadioProps & { label: ReactNode } | string); mergeStyle: CSSProperties }) : this.renderChildContent({ option, mergeStyle } as { option: ReactElement<RadioProps>; mergeStyle: CSSProperties })
	    }
	}

	renderOptions = (options: RadioGroupProps['options'], { spaceStyle, menuItem }: { spaceStyle: CSSProperties; menuItem: any[]; }) => {
	    const { optionType } = this.props;
	    const radioChilds = options!.map((option, index) => {
	        if (!option) {
	            return null
	        }
	        let style = {};
	        let menuKey = option as string;
	        let menuContent = option as string;
	        if (typeof option !== 'string') {
	            style = option.style || {};
	            menuKey = option.value as string;
	            menuContent = option.label as string;
	        }
	        return this.renderContentFromCount({ spaceStyle, menuKey, menuContent, style, arrayLength: options?.length!, optionType, option, index, isOption: true, menuItem })
	    })
	    return radioChilds
	}

	renderChilds = (children: ReactElement<RadioProps>, { spaceStyle, menuItem }: { spaceStyle: CSSProperties; menuItem: any[] }) => {
	    const childsNum = React.Children.count(children);
	    return React.Children.map(children, (child, index) => {
	        if (!child) {
	            return null
	        }
	        const { style, clsPrefix, value, children } = child?.props;
	        let mergeStyle = style || {};
	        const menuKey = value as string;
	        const menuContent = children as string;
	        const optionType = clsPrefix === `${prefix}-radio-button` ? 'button' : '';
	        return this.renderContentFromCount({ spaceStyle, menuKey, menuContent, style: mergeStyle, arrayLength: childsNum, optionType, option: child, index, isOption: false, menuItem })
	    })
	}
	renderBrowserOpts = (options: any) => {
	    const { clsPrefix, bordered, browserClassName, browserStyle } = this.props;
	    // 浏览态模式获取选中项的文本
	    const { selectedValue } = this.state;
	    let selectedText = '';
	    // 从options中查找
	    const selectedOption = options.find((option: any) => {
	        if (typeof option === 'string') {
	            return option === selectedValue;
	        }
	            return option?.value === selectedValue;
	    });
	    if (selectedOption) {
	        if (typeof selectedOption === 'string') {
	            selectedText = selectedOption as string;
	        } else {
	            selectedText = (selectedOption as any).label as string || '';
	        }
	    }
	    return (
	        <div className={`${prefix}-component-browser`}>
	            <div className={classnames(`${clsPrefix}-browser`,
	                {
	                    [`${prefix}-component-browser-border-bottom`]: bordered === 'bottom',
	                    [`${browserClassName}`]: browserClassName
	                }
	            )} style={browserStyle} fieldid={this.props.fieldid}>
	                {selectedText || '--'}
	            </div>
	        </div>
	    );
	}

	renderBrowserBtns = (children: any) => {
	    const { clsPrefix, bordered, browserClassName, browserStyle } = this.props;
	    // 浏览态模式获取选中项的文本
	    const { selectedValue } = this.state;
	    let selectedText = '';
	    // 从children中查找
	    React.Children.forEach(children, (child: any) => {
	        if (!child || !child.props) return;

	        if (child.props.value === selectedValue) {
	            const childContent = child.props.children;
	            if (typeof childContent === 'string' || typeof childContent === 'number') {
	                selectedText = String(childContent);
	            } else if (React.isValidElement(childContent)) {
	                // 尝试从React元素中提取文本
	                const extractText = (element: React.ReactNode): string => {
	                    if (typeof element === 'string' || typeof element === 'number') {
	                        return String(element);
	                    } else if (React.isValidElement(element)) {
	                        return extractText(element.props.children);
	                    } else if (Array.isArray(element)) {
	                        return element.map(extractText).join('');
	                    }
	                    return '';
	                };
	                selectedText = extractText(childContent);
	            }
	        }
	    });
	    return (
	        <div className={`${prefix}-component-browser`}>
	            <div className={classnames(`${clsPrefix}-browser`,
	                {
	                    [`${prefix}-component-browser-border-bottom`]: bordered === 'bottom',
	                    [`${browserClassName}`]: browserClassName
	                }
	            )} style={browserStyle}>
	                {selectedText || '--'}
	            </div>
	        </div>
	    );
	}
	render() {
	    const { Component, children, clsPrefix, className, disabled, readOnly, antd, options, optionType, spaceSize, maxCount, tiled, locale, browser, browserShow, bordered, ...others } = this.props;
	    const _locale = getLangInfo(locale, i18n, 'calendar')

	    if (browser && options && options.length && (optionType === 'button' || !browserShow)) {
	        return this.renderBrowserOpts(options)
	    } else if (browser && children && Array.isArray(children) && children.length > 0 && (children[0].type === RadioButton || !browserShow)) {
	        return this.renderBrowserBtns(children)
	    }

	    let spaceCls;
	    let btnsCls;
	    let browserCls = '';
	    let spaceStyle: CSSProperties = {};
	    const isRTL = this.props.dir === 'rtl';
	    if (spaceSize) {
	        if (typeof spaceSize === 'string' && Object.keys(sizeMap).includes(spaceSize)) {
	            spaceCls = `${clsPrefix}-space-${setComponentSize(spaceSize, {})}`
	        } else {
	            const spaceNum = typeof spaceSize === 'number' ? spaceSize : spaceSize?.endsWith?.('px') ? parseInt(spaceSize) : 0;
	            if (!isNaN(spaceNum) && spaceNum) {
	                spaceStyle = isRTL ? { marginLeft: `${spaceNum}px` } : { marginRight: `${spaceNum}px` };
	            }
	        }
	    }
 	    let radioChilds: ReactNode = null;
	    let moreChild: ReactNode = null;
	    const { dropDrownProps, popoverProps, tiledFooter, showMoreText } = this.props;
	    const menuItem: any[] = [];
	    if (options?.length) {
	        btnsCls = `${clsPrefix}-opts`
	        radioChilds = this.renderOptions(options, { spaceStyle, menuItem })
	    } else {
	        btnsCls = `${clsPrefix}-btns`
	        radioChilds = this.renderChilds(children as ReactElement<RadioProps>, { spaceStyle, menuItem })
	    }
	    if (browser) {
	        browserCls = `${clsPrefix}-browser`;
	    }
	    // dropdown 内容
	    if (menuItem.length) {
	        let getMore = false;
	        // if (tiled) {
	        //     getMore = menuItem.some(obj => Object.values(obj).includes(this.state.selectedValue))
	        // }
	        getMore = menuItem.some(obj => Object.values(obj).includes(this.state.selectedValue));
	        const inner = tiled ? (
	            <div className={`${clsPrefix}-tiled-wrapper`}>
	        				{
	                    menuItem.map((item, index) => {
	                        let key : string = item.key;
	                        return (
	                            <span
	                                onClick={(e: React.MouseEvent<HTMLElement>) => this.selectDropItem({domEvent: e, selectedKeys: [key]})}
	                                key={index}
	                                className={classnames({
	                                    [`${clsPrefix}-tiled-item`]: true,
	                                    [`${clsPrefix}-tiled-item-${this.props.size}`]: this.props.size !== 'default',
	                                    "is-checked": this.state.selectedValue === item.key,
	                                    [`${clsPrefix}-tiled-item-read-only`]: readOnly,
	                                    "disabled": disabled
	                                })}
	                                style={{
	                                    maxWidth: (this.state.popoverMaxWidth || 200) - 16, // 16 = 左右 padding
	                                }}
	                                title={typeof item.props.children === 'string' ? item.props.children : undefined}
	                            >
	                                {item.props.children}
	                            </span>
	                        )
	                    })
	                }
	                {tiledFooter ?
	                    <div className={`${clsPrefix}-tiled-footer`}>{tiledFooter}</div> : null}
	                </div>
	                ) : <Menu selectedKeys={[]} onSelect={this.selectDropItem}>{menuItem}</Menu>
	        moreChild = tiled ? (
	            <Popover placement='bottomRight' overlayStyle={{ maxWidth: this.state.popoverMaxWidth }} {...popoverProps} content={inner} trigger='click' getPopupContainer={() => this.radioGroup as HTMLElement}>
	                <Button
	                    className={classnames({
	                        [`${clsPrefix}-more-button`]: true,
	                        "is-checked": getMore,
	                        "show-text": showMoreText,
	                        [`${clsPrefix}-has-read-only`]: readOnly,
	                        [`${clsPrefix}-has-disabled`]: disabled,
	                    })}
	                    size={!this.props.size || this.props.size === 'default' ? undefined : this.props.size}
	                >{showMoreText && <span className={`${clsPrefix}-more-button-span`}>{_locale.langMap.more}</span>}<Icon type="uf-arrow-down" /></Button>
	            </Popover>
	        ) : (
	            <Dropdown {...dropDrownProps} overlay={inner} overlayClassName={`${clsPrefix}-my-radio-dropdown-menu`}>
				    <Button
	                    className={classnames({
	                        [`${clsPrefix}-more-button`]: true,
	                        "is-checked": getMore,
	                        "show-text": showMoreText,
	                        [`${clsPrefix}-has-read-only`]: readOnly,
	                        [`${clsPrefix}-has-disabled`]: disabled,
	                    })}
	                    size={!this.props.size || this.props.size === 'default' ? undefined : this.props.size}
	                >{showMoreText && <span className={`${clsPrefix}-more-button-span`}>{_locale.langMap.more}</span>}<Icon type="uf-arrow-down" /></Button>
	            </Dropdown>
	        )
	    }
	    const hasNumericSpace = typeof spaceSize === 'number' && spaceSize > 0;
	    const classNames = classnames(
	        clsPrefix,
	        className,
	        spaceCls,
	        btnsCls,
	        browserCls,
	        { [`${clsPrefix}-numeric-space`]: hasNumericSpace }
	    )
	    return (
	        <Component className={classNames} {...omit(others, ['onChange', 'selectedValue', 'onClick'])} focusValue={this.state.focusValue} ref={(ref: HTMLElement) => this.radioGroup = ref}>
	            {
	                radioChilds
	            }
	            {
	                maxCount ? moreChild : null
	            }
	        </Component>
	    );
	}
}

// RadioGroup.childContextTypes = childContextTypes;
// RadioGroup.propTypes = propTypes;
export default RadioGroup as React.ComponentClass<Partial<RadioGroupProps>>;
